import * as React from 'react';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { StoreContext } from '../../store/StoreProvider';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BarChartIcon from '@mui/icons-material/BarChart';
import VerifiedIcon from '@mui/icons-material/Verified';
import moment from 'moment';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { getI18N } from '../../i18n';

interface DashboardProps { }

interface FilterState {
  fechaDesde: Date | null;
  fechaHasta: Date | null;
  marca: string;
  modelo: string;
}

interface DonutData {
  label: string;
  value: number;
  color: string;
}

interface BarData {
  label: string;
  value: number;
  pct: number;
}

export const DashboardComponent = (props: DashboardProps) => {
  const [store] = React.useContext(StoreContext);
  const { Country } = store?.user;
  const currentLocale: string = Country || "PE";
  const i18n = getI18N({ currentLocale });

  const [filters, setFilters] = React.useState<FilterState>({
    fechaDesde: moment().startOf('year').toDate(),
    fechaHasta: moment().endOf('year').toDate(),
    marca: '',
    modelo: ''
  });

  const handleClearFilters = () => {
    setFilters({
      fechaDesde: moment().startOf('year').toDate(),
      fechaHasta: moment().endOf('year').toDate(),
      marca: '',
      modelo: ''
    });
  };

  const totalActivaciones = 12548;
  const tendencia = '+18.6%';

  const activacionesPorModelo: BarData[] = [
    { label: 'Enerjet 13T', value: 4392, pct: 35 },
    { label: 'Enerjet 15M', value: 4392, pct: 35 },
    { label: 'Enerjet 13W', value: 4392, pct: 35 },
    { label: 'Enerjet 23P', value: 4392, pct: 35 },
    { label: 'Otros modelos', value: 4392, pct: 35 }
  ];

  const tipoCombustion: DonutData[] = [
    { label: 'Gasolina', value: 45, color: '#E6A817' },
    { label: 'Diesel', value: 35, color: '#1C1C1C' },
    { label: 'GLP', value: 12, color: '#BDBDBD' },
    { label: 'GNV', value: 6, color: '#9E9E9E' },
    { label: 'Híbrido', value: 2, color: '#E0E0E0' }
  ];

  const tipoUso: DonutData[] = [
    { label: 'Gasolina', value: 45, color: '#E6A817' },
    { label: 'Diesel', value: 35, color: '#1C1C1C' },
    { label: 'GLP', value: 12, color: '#BDBDBD' },
    { label: 'GNV', value: 6, color: '#9E9E9E' },
    { label: 'Híbrido', value: 2, color: '#E0E0E0' }
  ];

  const plazoGarantia: DonutData[] = [
    { label: '12meses', value: 35, color: '#E6A817' },
    { label: '18meses', value: 45, color: '#1C1C1C' },
    { label: '24meses', value: 20, color: '#BDBDBD' }
  ];

  const buildConicGradient = (data: DonutData[]) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    let acc = 0;
    const stops: string[] = [];
    data.forEach((d) => {
      const start = (acc / total) * 360;
      acc += d.value;
      const end = (acc / total) * 360;
      stops.push(`${d.color} ${start}deg ${end}deg`);
    });
    return `conic-gradient(${stops.join(', ')})`;
  };

  const DonutChart = ({ title, data, icon }: { title: string; data: DonutData[]; icon?: React.ReactNode }) => {
    return (
      <div className="dashboard-chart-card-inner">
        <div className="dashboard-chart-title">{title}</div>
        <div className="dashboard-donut-body">
          <div className="dashboard-donut-ring" style={{ background: buildConicGradient(data) }}>
            <div className="dashboard-donut-hole">
              {icon && <div className="dashboard-donut-icon">{icon}</div>}
            </div>
          </div>
          <div className="dashboard-donut-legend">
            {data.map((d, i) => (
              <div key={i} className="dashboard-legend-row">
                <span className="dashboard-legend-dot" style={{ backgroundColor: d.color }} />
                <span className="dashboard-legend-label">{d.label}</span>
                <span className="dashboard-legend-value">{d.value}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="dashboard-chart-footer-link">VER DETALLE</div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <Box sx={{ flexGrow: 1, pl: 1, pr: 1 }}>
        <div className="dashboard-content">

          {/* Filters */}
          <Paper className="dashboard-filters" elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">
                  FILTRO POR FECHA
                </Typography>
                <div className="dashboard-date-range">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Desde"
                      value={filters.fechaDesde}
                      onChange={(newValue: any) => setFilters({ ...filters, fechaDesde: newValue })}
                      renderInput={(params: any) => <TextField {...params} size="small" className="dashboard-date-input" />}
                    />
                    <MobileDatePicker
                      label="Hasta"
                      value={filters.fechaHasta}
                      onChange={(newValue: any) => setFilters({ ...filters, fechaHasta: newValue })}
                      renderInput={(params: any) => <TextField {...params} size="small" className="dashboard-date-input" />}
                    />
                  </LocalizationProvider>
                </div>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">MARCA:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar marca"
                  value={filters.marca}
                  onChange={(e) => setFilters({ ...filters, marca: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">MODELO:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar modelo"
                  value={filters.modelo}
                  onChange={(e) => setFilters({ ...filters, modelo: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleClearFilters}
                  className="dashboard-clear-btn"
                >
                  Limpiar filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* KPI Summary */}
          <div className="dashboard-kpi-card">
            <svg className="dashboard-kpi-sparkline" viewBox="0 0 200 60" preserveAspectRatio="none">
              <polyline
                points="0,55 6,48 10,52 16,42 20,46 26,36 30,40 36,30 40,34 46,26 50,30 56,22 60,26 66,18 70,22 76,16 80,20 86,14 90,18 96,12 100,16 106,10 110,14 116,8 120,12 126,6 130,10 136,4 140,8 146,3 150,6 156,2 160,4 166,1 170,3 176,0"
                fill="none"
                stroke="rgba(0,0,0,0.12)"
                strokeWidth="1.2"
              />
            </svg>
            <div className="dashboard-kpi-content">
              <div className="dashboard-kpi-left">
                <span className="dashboard-kpi-label">FILTRO POR FECHA:</span>
                <span className="dashboard-kpi-value">{totalActivaciones.toLocaleString()}</span>
                <span className="dashboard-kpi-subtitle">Activaciones totales en el periodo seleccionado</span>
              </div>
              <div className="dashboard-kpi-right">
                <span className="dashboard-kpi-trend">{tendencia}</span>
                <span className="dashboard-kpi-trend-sub">vs periodo anterior</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <div className="dashboard-chart-card-inner">
                  <div className="dashboard-chart-title">ACTIVACIONES POR MODELO</div>
                  <div className="dashboard-bar-body">
                    {activacionesPorModelo.map((item, idx) => (
                      <div key={idx} className="dashboard-bar-row">
                        <span className="dashboard-bar-label">{item.label}</span>
                        <div className="dashboard-bar-track">
                          <div className="dashboard-bar-fill" style={{ width: `${item.pct}%` }} />
                        </div>
                        <span className="dashboard-bar-value">{item.value.toLocaleString()}</span>
                        <span className="dashboard-bar-pct">{item.pct}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="dashboard-chart-footer-link">VER DETALLE</div>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="TIPO DE COMBUSTIÓN" data={tipoCombustion} icon={<LocalGasStationIcon sx={{ fontSize: 36, color: '#555' }} />} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="TIPO DE USO" data={tipoUso} icon={<DirectionsCarIcon sx={{ fontSize: 36, color: '#555' }} />} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="PLAZO DE GARANTÍA" data={plazoGarantia} />
              </Paper>
            </Grid>
          </Grid>

          {/* Status Cards */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#d4edda' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#28a745' }}>
                  <CheckCircleIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">8,921</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Vigente</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">71% del total</Typography>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#ffe5cc' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#e67e22' }}>
                  <WarningAmberIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">8,921</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Vigente</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">71% del total</Typography>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#f8d7da' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#dc3545' }}>
                  <WarningAmberIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">8,921</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Vigente</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">71% del total</Typography>
                </div>
              </Paper>
            </Grid>
          </Grid>

        </div>
      </Box>
    </div>
  );
};
